import { AxiosResponse } from "axios";

import {
  ICreateSurveyRequest,
  IPublishCheck,
  IQuestionRequest,
  ISurveyBuilderSurvey,
  IVersionHeaderRequest,
} from "../@types/SurveyBuilder";
import { ISurveyDisplayRules } from "../@types/Surveys";
import { extractErrors, getSummaryErrorMessage } from "../utils/apiErrorUtils";
import { api } from "./api";

const BASE = "/Portal/SurveyBuilder";

/** A failed builder call; `isConflict` means someone saved the same row first (409). */
export class SurveyBuilderError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }

  get isConflict() {
    return this.status === 409;
  }
}

// The shared axios instance resolves with the error response instead of rejecting;
// react-query needs a rejection to run onError.
function unwrap<T>(response: AxiosResponse): T {
  if (!response || response.status >= 400) {
    throw new SurveyBuilderError(
      response ? getSummaryErrorMessage(extractErrors(response)) : "",
      response?.status ?? 0
    );
  }
  return response.data as T;
}

const lang = (languageCode: string) =>
  `languageCode=${encodeURIComponent(languageCode)}`;

/** EN-5261: the versioned survey builder API. Every write returns the survey as saved. */
export const surveyBuilderService = {
  async getSurvey(
    surveyId: string,
    versionId: string | undefined,
    languageCode: string
  ) {
    const version = versionId ? `versionId=${versionId}&` : "";
    return unwrap<ISurveyBuilderSurvey>(
      await api.get(
        `${BASE}/Surveys/${surveyId}?${version}${lang(languageCode)}`
      )
    );
  },

  async createSurvey(request: ICreateSurveyRequest) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.post(`${BASE}/Surveys`, request)
    );
  },

  async updateCode(
    surveyId: string,
    code: string | undefined,
    languageCode: string
  ) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.put(`${BASE}/Surveys/${surveyId}?${lang(languageCode)}`, {
        code,
      })
    );
  },

  async createDraft(
    surveyId: string,
    fromVersionId: string,
    languageCode: string
  ) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.post(
        `${BASE}/Surveys/${surveyId}/Draft?${lang(languageCode)}`,
        {
          fromVersionId,
        }
      )
    );
  },

  async discardDraft(surveyId: string, languageCode: string) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.delete(
        `${BASE}/Surveys/${surveyId}/Draft?${lang(languageCode)}`
      )
    );
  },

  async makeLive(surveyId: string, versionId: string, languageCode: string) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.put(
        `${BASE}/Surveys/${surveyId}/LiveVersion?${lang(languageCode)}`,
        {
          versionId,
        }
      )
    );
  },

  async publishCheck(versionId: string) {
    return unwrap<IPublishCheck>(
      await api.get(`${BASE}/Versions/${versionId}/PublishCheck`)
    );
  },

  async publish(versionId: string, languageCode: string) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.post(
        `${BASE}/Versions/${versionId}/Publish?${lang(languageCode)}`
      )
    );
  },

  async updateHeader(versionId: string, request: IVersionHeaderRequest) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.put(`${BASE}/Versions/${versionId}`, request)
    );
  },

  async reorder(
    versionId: string,
    sections: { sectionId: string; questionIds: string[] }[],
    rowVersion: number,
    languageCode: string
  ) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.put(
        `${BASE}/Versions/${versionId}/Order?${lang(languageCode)}`,
        {
          sections,
          rowVersion,
        }
      )
    );
  },

  async replaceDisplayRules(
    versionId: string,
    displayRules: ISurveyDisplayRules | undefined,
    rowVersion: number,
    languageCode: string
  ) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.put(`${BASE}/Versions/${versionId}/DisplayRules`, {
        languageCode,
        displayRules,
        rowVersion,
      })
    );
  },

  async addSection(versionId: string, languageCode: string) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.post(`${BASE}/Versions/${versionId}/Sections`, {
        languageCode,
        title: "",
      })
    );
  },

  async deleteSection(sectionId: string, languageCode: string) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.delete(`${BASE}/Sections/${sectionId}?${lang(languageCode)}`)
    );
  },

  async addQuestion(sectionId: string, request: IQuestionRequest) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.post(`${BASE}/Sections/${sectionId}/Questions`, request)
    );
  },

  async updateQuestion(questionId: string, request: IQuestionRequest) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.put(`${BASE}/Questions/${questionId}`, request)
    );
  },

  async deleteQuestion(questionId: string, languageCode: string) {
    return unwrap<ISurveyBuilderSurvey>(
      await api.delete(`${BASE}/Questions/${questionId}?${lang(languageCode)}`)
    );
  },
};
